(() => {
  const initializeLight = (root) => {
    const video = root.querySelector('[data-reaze-light]');
    const layer = root.querySelector('[data-reaze-motion]');
    if (!video || !layer || video.dataset.reazeInitialized) return;
    video.dataset.reazeInitialized = 'true';
    video.muted = true;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let failed = false;
    let disposed = false;
    const synchronize = () => {
      if (disposed) return;
      if (!visible || document.hidden || preference.matches || failed) {
        video.pause();
        if (preference.matches || failed) layer.classList.remove('is-playing');
        return;
      }
      video.play().catch(() => layer.classList.remove('is-playing'));
    };
    const onPlaying = () => {
      if (visible && !document.hidden && !preference.matches && !disposed) layer.classList.add('is-playing');
      else synchronize();
    };
    const onError = () => {
      failed = true;
      layer.classList.remove('is-playing');
    };
    video.addEventListener('playing', onPlaying);
    video.addEventListener('loadeddata', synchronize);
    video.addEventListener('error', onError);
    video.pause();
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      synchronize();
    });
    observer.observe(layer.closest('.reaze-hero, .rsv-launch'));
    preference.addEventListener('change', synchronize);
    document.addEventListener('visibilitychange', synchronize);
    document.addEventListener('pointerdown', synchronize, { passive: true });
    const cleanup = (event) => {
      if (!event.target.contains(root)) return;
      disposed = true;
      video.pause();
      observer.disconnect();
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('loadeddata', synchronize);
      video.removeEventListener('error', onError);
      preference.removeEventListener('change', synchronize);
      document.removeEventListener('visibilitychange', synchronize);
      document.removeEventListener('pointerdown', synchronize);
      document.removeEventListener('shopify:section:unload', cleanup);
    };
    document.addEventListener('shopify:section:unload', cleanup);
  };
  const initializeAtmosphere = (root) => {
    const frame = root.querySelector('[data-reaze-atmosphere]');
    if (!frame || frame.dataset.reazeInitialized) return;
    frame.dataset.reazeInitialized = 'true';
    const scenes = [...frame.querySelectorAll('[data-reaze-scene]')];
    const images = scenes.map(scene => scene.querySelector('img'));
    const buttons = [...frame.querySelectorAll('[data-reaze-mode-select]')];
    const dial = frame.querySelector('[data-reaze-mode-dial]');
    const title = frame.querySelector('[data-reaze-mode-title]');
    const counter = frame.querySelector('[data-reaze-mode-count]');
    const status = frame.querySelector('[data-reaze-mode-status]');
    if (!dial || !images.length || images.some(image => !image)) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const loads = new Map();
    const mobile = window.matchMedia('(max-width: 600px)');
    const section = frame.closest('.reaze-atmosphere');
    let scrollFrame = 0;
    let scrollStep = -1;
    const resetTimers = new Map();
    let selected = Math.max(0, Math.min(scenes.length - 1, Number(frame.dataset.reazeMode) || 0));
    let visible = false;
    let disposed = false;
    let pointer = null;
    const showPoster = (image) => {
      if (image.getAttribute('src') !== image.dataset.reazePoster) image.src = image.dataset.reazePoster;
    };
    const synchronize = () => {
      const animate = visible && !document.hidden && !preference.matches && !disposed;
      frame.dataset.reazeMotion = animate ? 'loading' : 'paused';
      images.forEach((image, index) => {
        clearTimeout(resetTimers.get(image));
        if (index !== selected || !animate) {
          const delay = animate && !preference.matches ? 400 : 0;
          resetTimers.set(image, setTimeout(() => showPoster(image), delay));
          return;
        }
        const source = image.dataset.reazeMotionSrc;
        if (!source) return;
        if (image.getAttribute('src') === source) {
          frame.dataset.reazeMotion = 'playing';
          return;
        }
        if (!loads.has(source)) {
          loads.set(source, new Promise(resolve => {
            const loader = new Image();
            loader.onload = () => resolve(true);
            loader.onerror = () => resolve(false);
            loader.src = source;
          }));
        }
        loads.get(source).then(loaded => {
          if (!loaded) {
            loads.delete(source);
            if (selected === index) frame.dataset.reazeMotion = 'fallback';
            return;
          }
          if (selected === index && visible && !document.hidden && !preference.matches && !disposed) {
            image.src = source;
            frame.dataset.reazeMotion = 'playing';
          }
        });
      });
    };
    const select = (value, announce = true) => {
      const previous = selected;
      selected = Math.max(0, Math.min(scenes.length - 1, value));
      frame.dataset.reazeMode = String(selected);
      scenes.forEach((scene, index) => {
        scene.classList.toggle('is-active', index === selected);
        scene.setAttribute('aria-hidden', String(index !== selected));
      });
      buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === selected)));
      const name = buttons[selected].textContent.trim();
      dial.value = String(selected);
      dial.setAttribute('aria-valuetext', name);
      dial.closest('.reaze-atmosphere__dial').style.setProperty('--reaze-dial-angle', `${(selected - 1) * 65}deg`);
      title.textContent = name;
      counter.textContent = `0${selected + 1} / 0${scenes.length}`;
      if (announce && previous !== selected) status.textContent = name;
      synchronize();
    };
    buttons.forEach(button => button.addEventListener('click', () => select(Number(button.dataset.reazeModeSelect)), options));
    dial.addEventListener('input', () => select(Number(dial.value)), options);
    dial.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        select((selected + 1) % scenes.length);
      }
    }, options);
    dial.addEventListener('pointerdown', event => {
      if (event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      if (event.pointerType !== 'touch') dial.focus({ preventScroll: true });
      dial.setPointerCapture(event.pointerId);
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, mode: selected, intent: null };
    }, options);
    dial.addEventListener('pointermove', event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const distance = event.clientX - pointer.x;
      const vertical = event.clientY - pointer.y;
      if (!pointer.intent) {
        if (Math.max(Math.abs(distance), Math.abs(vertical)) <= 8) return;
        pointer.intent = Math.abs(distance) > Math.abs(vertical) ? 'horizontal' : 'vertical';
      }
      if (pointer.intent !== 'horizontal') return;
      frame.classList.add('is-dragging');
      select(pointer.mode + Math.round(distance / 36));
    }, options);
    dial.addEventListener('pointerup', event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const tapped = !pointer.intent && Math.max(Math.abs(event.clientX - pointer.x), Math.abs(event.clientY - pointer.y)) <= 8;
      pointer = null;
      frame.classList.remove('is-dragging');
      if (tapped) select((selected + 1) % scenes.length);
    }, options);
    const cancelPointer = () => {
      pointer = null;
      frame.classList.remove('is-dragging');
    };
    dial.addEventListener('pointercancel', cancelPointer, options);
    dial.addEventListener('lostpointercapture', cancelPointer, options);
    images.forEach(image => image.addEventListener('error', () => showPoster(image), options));
    const updateFromScroll = () => {
      scrollFrame = 0;
      if (!visible || !mobile.matches || preference.matches || disposed) return;
      const top = parseFloat(getComputedStyle(frame).top) || 0;
      const travel = Math.max(1, section.offsetHeight - frame.offsetHeight);
      const progress = Math.max(0, Math.min(1, (top - section.getBoundingClientRect().top) / travel));
      const index = Math.min(scenes.length - 1, Math.floor(progress * scenes.length));
      if (index !== scrollStep) {
        scrollStep = index;
        select(index, false);
      }
    };
    const onScroll = () => {
      if (visible && mobile.matches && !preference.matches && !scrollFrame) scrollFrame = requestAnimationFrame(updateFromScroll);
    };
    const onLayoutChange = () => {
      scrollStep = -1;
      onScroll();
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal: controller.signal });
    window.addEventListener('resize', onLayoutChange, { passive: true, signal: controller.signal });
    mobile.addEventListener('change', onLayoutChange, options);
    preference.addEventListener('change', onLayoutChange, options);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      synchronize();
      onScroll();
    }, { threshold: 0.05 });
    observer.observe(frame);
    preference.addEventListener('change', synchronize, options);
    document.addEventListener('visibilitychange', synchronize, options);
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      disposed = true;
      cancelAnimationFrame(scrollFrame);
      observer.disconnect();
      controller.abort();
      resetTimers.forEach(clearTimeout);
      images.forEach(showPoster);
      delete frame.dataset.reazeInitialized;
    }, options);
    select(selected, false);
  };
  const initialize = (root) => {
    initializeLight(root);
    initializeAtmosphere(root);
    const section = root.querySelector('[data-reaze-intention]');
    if (!section || section.dataset.reazeInitialized) return;
    section.dataset.reazeInitialized = 'true';
    const steps = [...section.querySelectorAll('[data-reaze-step]')];
    const controls = [...section.querySelectorAll('[data-reaze-select]')];
    const panels = [...section.querySelectorAll('[data-reaze-panel]')];
    const visuals = [...section.querySelectorAll('[data-reaze-visual]')];
    const sticky = section.querySelector('.reaze-intention__sticky');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 600px)');
    let scrollStep = -1;
    let frame = 0;
    let visible = false;
    const measureLayout = () => {
      section.style.setProperty('--reaze-sticky-height', `${sticky.offsetHeight}px`);
    };
    const select = (index) => {
      section.dataset.reazeActive = String(index);
      steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
      controls.forEach((control, i) => control.setAttribute('aria-expanded', String(i === index)));
      panels.forEach((panel, i) => {
        panel.setAttribute('aria-hidden', String(i !== index));
        panel.inert = i !== index;
      });
      visuals.forEach((visual, i) => {
        visual.classList.toggle('is-active', i === index);
        visual.setAttribute('aria-hidden', String(i !== index));
      });
      measureLayout();
    };
    const updateFromScroll = () => {
      frame = 0;
      if (!visible || reducedMotion.matches) return;
      const rect = section.getBoundingClientRect();
      const stickyTop = parseFloat(getComputedStyle(sticky).top) || 0;
      const travel = Math.max(1, section.offsetHeight - sticky.offsetHeight);
      const progress = Math.max(0, Math.min(1, (stickyTop - rect.top) / travel));
      const index = Math.min(2, Math.floor(progress * 3));
      if (index !== scrollStep) {
        scrollStep = index;
        select(index);
      }
    };
    const onScroll = () => {
      if (visible && !frame) frame = requestAnimationFrame(updateFromScroll);
    };
    controls.forEach((control, i) => control.addEventListener('click', () => select(i)));
    const resize = new ResizeObserver(measureLayout);
    resize.observe(sticky);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) onScroll();
    });
    observer.observe(section);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    const onBreakpoint = () => {
      scrollStep = -1;
      select(0);
      onScroll();
    };
    mobile.addEventListener('change', onBreakpoint);
    document.fonts.ready.then(measureLayout);
    select(0);
    const cleanup = (event) => {
      if (!event.target.contains(root)) return;
      mobile.removeEventListener('change', onBreakpoint);
      resize.disconnect();
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
      document.removeEventListener('shopify:section:unload', cleanup);
    };
    document.addEventListener('shopify:section:unload', cleanup);
  };
  document.querySelectorAll('[data-reaze-home], [data-reaze-animated-banner]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-reaze-home], [data-reaze-animated-banner]').forEach(initialize));
})();
